/* eslint-disable no-plusplus */
import type { EditorState } from '@tiptap/pm/state'
import { MutableRefObject } from 'react'
import { isWindowDefined } from '@chegg/utils'
import { QA_WORD_COUNT_LIMIT } from '@chegg-wt/core/constants/paraphraser'
import { EditorSelection, Feedback, FeedbackType } from '../../types/paraphraser'
import { SentenceOffset } from '../../state/paraphraser/types'
import { PaperCheck_paperCheck_sentences as PaperCheckSentence } from '../../oneGraphQueries/__generated__/PaperCheck'
import getFeatureEditor from './getEditor'

export const getSelection = (state: EditorState): EditorSelection | null => {
  if (!state) return null
  const { selection } = state
  const { from, to } = selection
  const selectedText = state.doc.textBetween(from, to, ' ').trim()

  return { selectedText, from, to }
}

export const getWordCount = (text: string): number => {
  const trimmedText = text.trim()
  return trimmedText === '' ? 0 : trimmedText.split(/\s+/g).length
}

export const trimTextByWords = (text: string, wordLimit: number): string => {
  const words = text.split(/\s+/g)
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ')
  }
  return text
}

export const mapSentencesToSentenceOffsets = (
  sentences: PaperCheckSentence[],
  text: string
): SentenceOffset[] => {
  const timestamp = new Date().getTime()
  return sentences.map((sentence) => {
    const { offset, length } = sentence.position
    const sentenceText = text.substring(offset, offset + length)
    const mappedSentence: SentenceOffset = {
      id: sentence.id,
      originalText: sentenceText,
      lastUpdated: timestamp,
      ...sentence.position,
    }
    return mappedSentence
  })
}

export const getGrammarPosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
): { offset: number; length: number } => {
  const sentenceOffset = sentenceOffsets.find(
    (sentence) => sentence.id === feedback.sentenceId
  )?.offset

  if (!sentenceOffset)
    throw new Error(
      `sentence offset not found for feedback.sentenceId: ${feedback.sentenceId}`
    )

  return {
    offset: sentenceOffset + feedback.position.offset,
    length: feedback.position.length,
  }
}

export const getParaphrasePosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
): { offset: number; length: number } => {
  const sentence = sentenceOffsets.find((phrase) => phrase.id === feedback.sentenceId)

  if (!sentence)
    throw new Error(
      `sentence offset not found for feedback.sentenceId: ${feedback.sentenceId}`
    )

  return { offset: sentence.offset, length: sentence.length }
}

export const getFeedbackPosition = (
  feedback: Feedback,
  sentenceOffsets: SentenceOffset[]
) => {
  return feedback.feedbackType === FeedbackType.PARAPHRASE
    ? getParaphrasePosition(feedback, sentenceOffsets)
    : getGrammarPosition(feedback, sentenceOffsets)
}

export const getTippyInstance = () =>
  isWindowDefined
    ? // eslint-disable-next-line no-underscore-dangle
      (document.querySelector('.tippy-box')?.parentElement as any)?._tippy || null
    : null

export const storeProofReaderPapersInLocal = (): boolean => {
  // This function will be updated to return true when user is guest user
  return true
}

// Remove all underlines from editor view and storage
export const clearAllUnderlines = () => {
  const editor = getFeatureEditor()
  editor.commands.removeCustomUnderline({
    id: 'remove-editor-underline',
    offset: 1,
    length: editor.getText().length,
  })

  editor.storage.comments = {}
}

export const addUnderlineEditorText = (
  feedbacks: Feedback[],
  sentenceOffsets: SentenceOffset[],
  activeTabIndex?: number
) => {
  if (activeTabIndex === 0 || activeTabIndex === 1) {
    const editor = getFeatureEditor()
    clearAllUnderlines()

    feedbacks.forEach((feedback) => {
      const { id } = feedback

      let underlinePosition = null
      if (activeTabIndex === 0 && feedback.feedbackType === FeedbackType.GRAMMAR) {
        underlinePosition = getGrammarPosition(feedback, sentenceOffsets)
      }
      if (activeTabIndex === 1 && feedback.feedbackType === FeedbackType.PARAPHRASE) {
        underlinePosition = getParaphrasePosition(feedback, sentenceOffsets)
      }

      if (underlinePosition) {
        editor.commands.setCustomUnderline({
          id,
          offset: underlinePosition.offset,
          length: underlinePosition.length,
          type: feedback.feedbackType,
        })
      }
    })
  }
}

export const getCurrentDateInMMDDYY = (): string => {
  const date = new Date()

  const dateFormatted = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)

  return dateFormatted
}

export const shouldDisableSynonyms = (
  selection: EditorSelection,
  sentenceOffsets: SentenceOffset[],
  selectedSentenceOffSet: MutableRefObject<SentenceOffset>
) => {
  if (sentenceOffsets.length === 0 || !selection || !selection.selectedText.trim())
    return true

  // wordcount greater than QA_WORD_COUNT_LIMIT
  const wordCount = getWordCount(selection.selectedText)
  if (wordCount > QA_WORD_COUNT_LIMIT) return true

  const { from, to } = selection
  let sentenceMatchFound: boolean = false

  for (let sentenceIndex = 0; sentenceIndex < sentenceOffsets.length; sentenceIndex++) {
    const sentenceOffSet = sentenceOffsets[sentenceIndex]
    const { length, offset } = sentenceOffSet

    if (from + 1 <= offset + length) {
      if (to > offset + length) {
        // selection between multiple sentences
        return true
      }

      // eslint-disable-next-line no-param-reassign
      selectedSentenceOffSet.current = sentenceOffSet
      sentenceMatchFound = true
      break
    }
  }

  return !sentenceMatchFound
}

export const shouldDisableRewrite = (
  selection: EditorSelection,
  sentenceOffsets: SentenceOffset[]
) => {
  if (sentenceOffsets.length === 0 || !selection || !selection.selectedText.trim())
    return true

  // wordcount less than eql QA_WORD_COUNT_LIMIT
  const wordCount = getWordCount(selection.selectedText)
  if (wordCount <= QA_WORD_COUNT_LIMIT) return true

  const { from, to } = selection
  let fromSentenceMatchFound: boolean = false
  let toSentenceMatchFound: boolean = false

  for (let sentenceIndex = 0; sentenceIndex < sentenceOffsets.length; sentenceIndex++) {
    const sentenceOffSet = sentenceOffsets[sentenceIndex]
    const { length, offset } = sentenceOffSet
    // check if the current sentence is part of the selected text.
    // We allow multiple sentences to be selected, so it is possible that from & to index belongs to
    // sentence boundaries of different sentences
    if (from + 1 <= offset + length) {
      fromSentenceMatchFound = true
    }

    if (to <= offset + length) {
      toSentenceMatchFound = true
      break
    }
  }

  return !(fromSentenceMatchFound && toSentenceMatchFound)
}

// example - substring = 'n incomp', completeText = 'This is an incomplete, word-example in a sentence.' output = 'an incomplete'
export const getCompleteWords = (
  sentence: string,
  oldStartIndex: number,
  oldEndIndex: number
) => {
  let newStartIndex = oldStartIndex
  let newEndIndex = oldEndIndex - 1

  while (newStartIndex >= 0 && sentence[newStartIndex] !== ' ') newStartIndex--
  while (newEndIndex <= sentence.length && sentence[newEndIndex] !== ' ') newEndIndex++

  const completeWords = sentence.substring(newStartIndex, newEndIndex).trim()
  return completeWords
}

export const getSelectedSentences = (
  sentenceOffsets: SentenceOffset[],
  from: number,
  to: number
): SentenceOffset[] => {
  const selectedSentenceOffsets = []
  let fromFound = false
  for (let sentenceIndex = 0; sentenceIndex < sentenceOffsets.length; sentenceIndex++) {
    const sentenceOffSet = sentenceOffsets[sentenceIndex]
    const { length, offset } = sentenceOffSet
    const totalLength = offset + length
    if (fromFound || from <= totalLength - 1) {
      selectedSentenceOffsets.push(sentenceOffSet)
      fromFound = true
      if (to <= totalLength) break
    }
  }

  return selectedSentenceOffsets
}

export const scrollMarkContentInEditor = (feedbackId: string) => {
  if (isWindowDefined && feedbackId) {
    const currHightlightMark = window.document.querySelector(
      `[data-test="customunderline-mark-${feedbackId}"]`
    ) as HTMLDivElement | null
    const parentElement = window.document.getElementById('editor-container')

    parentElement.scrollTo({
      top: currHightlightMark.offsetTop,
      behavior: 'smooth',
    })
  }
}

export const clearWindowSelection = () => {
  const windowSelection = window.getSelection()
  if (windowSelection.rangeCount > 0) {
    windowSelection.removeAllRanges()
  }
}
